/* Sizzle mode — a bare-bones full-viewport player for a single cinematic act.
   Used only by the offline recording script to capture 4K sizzle reel footage.
   Chrome + nav + footer + SIX launcher are all suppressed so the shot is pure
   scene.  Route: /_sizzle/:act  where :act ∈ {arsenal, flywheel, seal}. */
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import CinematicShell from "@/components/cinematic/CinematicShell";
import {
  HomeArsenalStill,
  HomeFlywheelStill,
  HomeSealStill,
} from "@/components/cinematic/stills";

const ACTS = {
  arsenal: {
    sceneImport: () => import("@/components/cinematic/scenes/HomeArsenal"),
    still: HomeArsenalStill,
    title: "AMERICA FIRST. AMERICA ONLY.",
    subtitle: "Six weapons. One stack. One flag.",
  },
  flywheel: {
    sceneImport: () => import("@/components/cinematic/scenes/HomeFlywheel"),
    still: HomeFlywheelStill,
    title: "THE ARSENAL",
    subtitle: "Source. Clean. Identify. Score. Route. Activate.",
  },
  seal: {
    sceneImport: () => import("@/components/cinematic/scenes/HomeSeal"),
    still: HomeSealStill,
    title: "THE DT6 STANDARD",
    subtitle: "America First. America Only.",
  },
};

export default function SizzlePage() {
  const { act } = useParams();
  const cfg = ACTS[act] || ACTS.arsenal;

  useEffect(() => {
    // Force the site into CINEMATIC tier for the recording, regardless of
    // whatever the last local preference was.
    try {
      localStorage.removeItem("dt6_motion_pref");
      window.dispatchEvent(new CustomEvent("dt6:motion-pref-change"));
    } catch (_e) { /* no-op */ }
    // Kill any chrome that survived — belt + suspenders for the recorder.
    document.body.style.margin = "0";
    document.body.style.overflow = "hidden";
    document.documentElement.style.background = "#0B0E14";
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0B0E14",
        overflow: "hidden",
      }}
      data-testid={`sizzle-act-${act}`}
    >
      <CinematicShell
        sceneImport={cfg.sceneImport}
        stillSrc={cfg.still}
        testId={`sizzle-scene-${act}`}
        className="dt6-cinematic-grain"
        style={{ position: "absolute", inset: 0, zIndex: 0 }}
      />
      {/* Copy overlay — matches the doctrine (Chivo + JetBrains Mono, dead-center). */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "0 8vw",
          color: "#EDE8DC",
          pointerEvents: "none",
        }}
      >
        <div
          className="font-mono"
          style={{
            fontSize: "0.9rem",
            letterSpacing: "0.24em",
            color: "#B22234",
            marginBottom: "1rem",
            opacity: 0,
            animation: "sizzle-fade-in 900ms 400ms forwards ease-out",
          }}
        >
          / DT6
        </div>
        <h1
          className="font-heading"
          style={{
            fontSize: "clamp(3.5rem, 6.5vw, 6.5rem)",
            fontWeight: 900,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            margin: 0,
            maxWidth: "80vw",
            opacity: 0,
            animation: "sizzle-fade-in 1200ms 900ms forwards ease-out",
          }}
        >
          {cfg.title}
        </h1>
        <p
          style={{
            fontSize: "clamp(1.1rem, 1.6vw, 1.6rem)",
            marginTop: "1.5rem",
            color: "#8A8579",
            maxWidth: "60vw",
            opacity: 0,
            animation: "sizzle-fade-in 1200ms 1600ms forwards ease-out",
          }}
        >
          {cfg.subtitle}
        </p>
      </div>
      <style>{`
        @keyframes sizzle-fade-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
